import { useRef, useEffect, useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";

/**
 * Full festival schedule — every stage, noon to midnight, as a clean vertical
 * running order: time on the left, performer on the right. Sort by time
 * (default) or group by stage. Times sourced from the internal event timeout
 * (production spreadsheet). Artist bios stay on /lineup.
 *
 * All times are minutes from midnight.
 */

const fmt = (m: number, showPeriod = true) => {
  const h24 = Math.floor(m / 60) % 24;
  const min = m % 60;
  let h = h24 % 12;
  if (h === 0) h = 12;
  const time = min ? `${h}:${String(min).padStart(2, "0")}` : `${h}`;
  return showPeriod ? `${time} ${h24 < 12 ? "AM" : "PM"}` : time;
};

type VenueKey = "main" | "deck" | "garden" | "marine" | "admirals";

const VENUE_ORDER: VenueKey[] = ["main", "deck", "garden", "marine", "admirals"];

const VENUES: Record<VenueKey, { name: string; color: string; sub: string }> = {
  main: {
    name: "Main Stage",
    color: "#F7B41C",
    sub: "Headliners, ten feet from the water",
  },
  deck: {
    name: "The Deck",
    color: "#389FBA",
    sub: "High Noon Deck Party — floating DJs on the river",
  },
  garden: {
    name: "Garden",
    color: "#6ECA9F",
    sub: "Outdoor grooves all afternoon",
  },
  marine: {
    name: "Marine Room",
    color: "#7CB6EC",
    sub: "Indoor stage — bands, DJs & trivia",
  },
  admirals: {
    name: "Admirals Lounge",
    color: "#E7B877",
    sub: "VIP ticket holders",
  },
};

type Slot = {
  start: number;
  end: number;
  name: string;
  venue: VenueKey;
  note?: string;
  tba?: boolean;
};

type Day = {
  key: string;
  label: string;
  date: string;
  doorsLabel: string;
  slots: Slot[];
};

const DAYS: Day[] = [
  {
    key: "fri",
    label: "Friday",
    date: "July 17",
    doorsLabel: "Doors 3 PM",
    slots: [
      // Main Stage
      { start: 1050, end: 1140, name: "Seven Wonders", venue: "main", note: "Fleetwood Mac tribute" },
      { start: 1170, end: 1260, name: "Pablo Cruise", venue: "main" },
      { start: 1290, end: 1380, name: "Yacht Rock Revue", venue: "main", note: "Friday headliner" },
      // The Deck
      { start: 900, end: 960, name: "TBA", venue: "deck", tba: true },
      { start: 960, end: 1080, name: "DJ Daryl G", venue: "deck" },
      { start: 1080, end: 1185, name: "Mike R.", venue: "deck" },
      { start: 1185, end: 1305, name: "DJ Manuel Antonio", venue: "deck" },
      { start: 1305, end: 1425, name: "Mr Wired Up", venue: "deck" },
      // Garden
      { start: 900, end: 1020, name: "DJ TJ Monte", venue: "garden" },
      { start: 1050, end: 1140, name: "TBA", venue: "garden", tba: true },
      // Marine Room
      { start: 960, end: 1080, name: "The Yacht-Seas", venue: "marine" },
      { start: 1110, end: 1230, name: "DJ TJ Monte", venue: "marine" },
      { start: 1245, end: 1365, name: "The Yacht-Seas", venue: "marine", note: "second set" },
      { start: 1365, end: 1440, name: "TBA", venue: "marine", tba: true },
      // Admirals Lounge (VIP)
      { start: 990, end: 1110, name: "Not 2 Nite", venue: "admirals" },
      { start: 1215, end: 1335, name: "Dueling Pianos", venue: "admirals", note: "Charles & Wyatt" },
      { start: 1350, end: 1440, name: "The Act", venue: "admirals" },
    ],
  },
  {
    key: "sat",
    label: "Saturday",
    date: "July 18",
    doorsLabel: "Doors Noon",
    slots: [
      // Main Stage
      {
        start: 1110,
        end: 1230,
        name: "Ambrosia & Friends",
        venue: "main",
        note: "with Peter Beckett, Walter Egan, Elliot Lurie & John Ford Coley",
      },
      { start: 1260, end: 1380, name: "Three Dog Night", venue: "main", note: "Saturday headliner" },
      // The Deck
      { start: 720, end: 810, name: "DJ Rick Scampi", venue: "deck" },
      { start: 810, end: 930, name: "DJ Charlie", venue: "deck" },
      { start: 930, end: 1050, name: "DJ Wired Up", venue: "deck" },
      { start: 1050, end: 1185, name: "DiscJockey George", venue: "deck" },
      { start: 1185, end: 1305, name: "DJ Ric Nasty", venue: "deck" },
      { start: 1290, end: 1410, name: "Party Pupils", venue: "deck", note: "yacht house" },
      // Garden
      { start: 720, end: 900, name: "Urban Tropical", venue: "garden" },
      { start: 930, end: 1050, name: "DJ TJ Monte", venue: "garden" },
      // Marine Room
      { start: 750, end: 900, name: "Yacht Rock Trivia", venue: "marine", note: "hosted by Scott Zumberg" },
      { start: 930, end: 1050, name: "Not 2 Nite", venue: "marine" },
      { start: 1080, end: 1200, name: "Problematic Black Hottie", venue: "marine" },
      { start: 1200, end: 1320, name: "DJ B Free", venue: "marine" },
      { start: 1350, end: 1440, name: "The Act", venue: "marine" },
      // Admirals Lounge (VIP)
      { start: 720, end: 870, name: "Charles & Wyatt", venue: "admirals", note: "piano sets" },
      { start: 900, end: 1020, name: "The Yacht-Seas", venue: "admirals" },
      { start: 1050, end: 1185, name: "DJ WaltAmore", venue: "admirals" },
      { start: 1185, end: 1305, name: "DJ Charlie", venue: "admirals" },
      { start: 1335, end: 1440, name: "Yacht Rock Karaoke", venue: "admirals", note: "with Charles & Wyatt" },
    ],
  },
];

/** "5:30 – 7 PM" — if it must wrap, it breaks after the dash, never inside a time. */
const RangeLabel = ({ slot }: { slot: Slot }) => {
  const startPM = Math.floor(slot.start / 60) % 24 >= 12;
  const endPM = Math.floor(slot.end / 60) % 24 >= 12 && slot.end !== 1440;
  return (
    <>
      <span className="whitespace-nowrap">
        {fmt(slot.start, startPM !== endPM)}&nbsp;–
      </span>{" "}
      <span className="whitespace-nowrap">{fmt(slot.end)}</span>
    </>
  );
};

/** "3 PM" / "Noon" bucket heading for the By Time view. */
const hourLabel = (h: number) => (h === 12 ? "Noon" : fmt(h * 60));

/** Group slots by their start hour so acts starting together share one slot. */
const byHour = (slots: Slot[]) => {
  const groups = new Map<number, Slot[]>();
  for (const s of slots) {
    const h = Math.floor(s.start / 60);
    const g = groups.get(h);
    if (g) g.push(s);
    else groups.set(h, [s]);
  }
  return [...groups.entries()];
};

/* ── Poster palette, lifted from the circle-design carousel ── */
const NAVY = "#14457F";
const BUTTER = "#FBF4BC";
const SKY = "#A9D4F5";
const GOLD = "#D9A62E";
const SPLASH = "#4FA8E0";

/** Sticker-style sun (matches the circle-design carousel). */
const SunSticker = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden>
    <g fill="#F7B41C" stroke="#FFFFFF" strokeWidth="2.5" strokeLinejoin="round">
      {Array.from({ length: 12 }, (_, i) => (
        <path
          key={i}
          d="M32 6 C34.5 12, 29.5 12, 32 18 Z"
          transform={`rotate(${i * 30} 32 32)`}
        />
      ))}
      <circle cx="32" cy="32" r="13" />
    </g>
  </svg>
);

/** Water-splash accent (matches the circle-design carousel). */
const SplashSticker = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden>
    <path d="M14 40 C10 30, 12 24, 18 18 C18 28, 17 32, 14 40 Z" fill={NAVY} />
    <path d="M24 38 C22 30, 24 25, 29 21 C28 29, 27 32, 24 38 Z" fill={SPLASH} />
    <circle cx="10" cy="20" r="2.6" fill={NAVY} />
    <circle cx="34" cy="30" r="2.2" fill={SPLASH} />
  </svg>
);

/** One act as a poster block: navy block, butter type; Main Stage gets gold. */
const ActBlock = ({ slot, showVenue }: { slot: Slot; showVenue: boolean }) => {
  const v = VENUES[slot.venue];
  const isMain = slot.venue === "main";
  const blockStyle = slot.tba
    ? { background: "transparent", border: `2px dashed ${NAVY}59` }
    : isMain
      ? { background: "#F7B41C", boxShadow: "0 2px 8px rgba(20,69,127,0.18)" }
      : { background: NAVY, boxShadow: "0 2px 8px rgba(20,69,127,0.22)" };
  const nameColor = slot.tba ? `${NAVY}66` : isMain ? NAVY : BUTTER;
  const metaColor = slot.tba ? `${NAVY}59` : isMain ? `${NAVY}B3` : "rgba(251,244,188,0.72)";
  return (
    <div className="rounded-xl md:rounded-2xl px-3.5 py-2.5 md:px-5 md:py-3.5" style={blockStyle}>
      <span
        className="block font-['Anton'] uppercase tracking-wide leading-tight text-[17px] md:text-[23px]"
        style={{ color: nameColor }}
      >
        {slot.tba ? "To Be Announced" : slot.name}
      </span>
      <span
        className="block mt-0.5 text-[10.5px] md:text-xs font-bold uppercase tracking-[0.12em] tabular-nums"
        style={{ color: metaColor }}
      >
        <RangeLabel slot={slot} />
        {showVenue && <> · {v.name}</>}
        {slot.note && (
          <span className="normal-case font-semibold tracking-normal"> · {slot.note}</span>
        )}
      </span>
    </div>
  );
};

/** Gold "rope ring" poster panel with sun + splash stickers. */
const PosterPanel = ({
  tone,
  children,
}: {
  tone: "sky" | "butter";
  children: ReactNode;
}) => (
  <div
    className="rounded-[1.6rem] md:rounded-[2.2rem] p-1.5 md:p-2"
    style={{ background: tone === "sky" ? SKY : BUTTER, border: `3px solid ${GOLD}` }}
  >
    <div
      className="relative rounded-[1.15rem] md:rounded-[1.7rem] px-3.5 py-4 sm:px-5 md:px-8 md:py-7 overflow-hidden"
      style={{ border: `2px dashed ${GOLD}99` }}
    >
      <SunSticker className="absolute top-3 right-3 w-12 h-12 md:w-16 md:h-16 md:top-5 md:right-5 pointer-events-none" />
      <SplashSticker className="absolute bottom-2 left-1.5 w-10 h-10 md:w-14 md:h-14 opacity-70 pointer-events-none" />
      {children}
    </div>
  </div>
);

const Schedule = () => {
  usePageSEO({
    title: "Schedule — Yacht Rock Detroit 2026 | All 5 Stages, Noon to Midnight",
    description:
      "The full Yacht Rock Detroit 2026 schedule at The Roostertail — five stages of live bands and DJs. Friday July 17 doors 3 PM, Saturday July 18 doors noon, music until midnight both days. Three Dog Night, Yacht Rock Revue, Pablo Cruise, Ambrosia + 25 live acts.",
    keywords:
      "yacht rock detroit schedule, yacht rock detroit set times, roostertail festival schedule, three dog night set time detroit, detroit daytime festival 2026",
    ogImage: PHOTOS.STAGE.bandDramaticLights,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Schedule", url: "https://yachtrockdetroit.com/schedule" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  const [dayIndex, setDayIndex] = useState(0);
  const [view, setView] = useState<"time" | "stage">("time");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  const day = DAYS[dayIndex];
  const byTime = [...day.slots].sort(
    (a, b) =>
      a.start - b.start ||
      VENUE_ORDER.indexOf(a.venue) - VENUE_ORDER.indexOf(b.venue),
  );

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative bg-yacht-blue overflow-hidden pt-28 md:pt-40 pb-12 md:pb-16"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            July 17–18 · The Roostertail
          </motion.p>
          <div
            className="text-[10vw] md:text-7xl font-bold text-white leading-[0.95] tracking-tight"
            role="heading"
            aria-level={1}
          >
            <GradualSpacing text="Schedule" duration={0.5} delayMultiple={0.04} />
          </div>
          <motion.p
            className="text-white/85 text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            This is a daytime party. Doors at 3 PM Friday and noon Saturday —
            then five stages of music straight through to midnight.
          </motion.p>
          <motion.p
            className="text-yacht-yellow font-bold text-xs md:text-sm tracking-[0.22em] uppercase mt-5"
            initial={{ opacity: 0 }}
            animate={isHeaderInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            5 stages · 25+ acts · Sun up to lights out
          </motion.p>
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="py-10 md:py-16 px-5 sm:px-6 md:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Day tabs */}
          <div className="grid grid-cols-2 gap-2">
            {DAYS.map((d, i) => (
              <button
                key={d.key}
                onClick={() => setDayIndex(i)}
                className="rounded-2xl px-3 py-3 md:py-4 text-left"
                style={
                  i === dayIndex
                    ? { background: NAVY, border: `2px solid ${NAVY}` }
                    : { background: "transparent", border: `2px solid ${NAVY}40` }
                }
              >
                <span
                  className="block font-['Anton'] text-lg md:text-2xl uppercase tracking-wide leading-none whitespace-nowrap"
                  style={{ color: i === dayIndex ? BUTTER : NAVY }}
                >
                  <span className="sm:hidden">{d.label.slice(0, 3)}</span>
                  <span className="hidden sm:inline">{d.label}</span>
                  <span className="ml-1.5" style={{ opacity: 0.55 }}>
                    {d.date}
                  </span>
                </span>
                <span
                  className="block text-[11px] md:text-xs font-bold mt-1.5 uppercase tracking-wider"
                  style={{ color: i === dayIndex ? "#F7B41C" : `${NAVY}99` }}
                >
                  {d.doorsLabel} · Music til Midnight
                </span>
              </button>
            ))}
          </div>

          {/* View toggle */}
          <div className="flex items-center justify-between gap-4 mt-5 mb-3">
            <div className="inline-flex rounded-full p-0.5" style={{ border: `2px solid ${NAVY}40` }}>
              {(
                [
                  ["time", "By Time"],
                  ["stage", "By Stage"],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setView(key)}
                  className="px-4 py-1.5 rounded-full text-xs md:text-sm font-bold whitespace-nowrap transition-colors"
                  style={
                    view === key
                      ? { background: NAVY, color: BUTTER }
                      : { color: `${NAVY}99` }
                  }
                >
                  {label}
                </button>
              ))}
            </div>
            <span className="hidden sm:inline text-[11px] md:text-xs font-medium" style={{ color: `${NAVY}80` }}>
              All times local · {day.label} {day.date}
            </span>
          </div>

          {/* LISTINGS — poster panel in the circle-design style */}
          <PosterPanel tone={day.key === "fri" ? "sky" : "butter"}>
            {view === "time" ? (
              <div>
                {byHour(byTime).map(([hour, slots]) => (
                  <div
                    key={hour}
                    className="relative z-10 grid grid-cols-[64px_1fr] md:grid-cols-[104px_1fr] gap-x-3 md:gap-x-6 py-3.5 md:py-4 border-b last:border-b-0"
                    style={{ borderColor: `${NAVY}26` }}
                  >
                    <span
                      className="font-['Anton'] uppercase leading-none pt-1 text-xl md:text-3xl"
                      style={{ color: NAVY }}
                    >
                      {hourLabel(hour)}
                      {hour === 12 && (
                        <span className="block text-[9px] md:text-[11px] font-bold tracking-[0.18em] mt-1" style={{ color: `${NAVY}8C` }}>
                          DOORS
                        </span>
                      )}
                    </span>
                    <div className="space-y-2 md:space-y-2.5">
                      {slots.map((slot) => (
                        <ActBlock
                          key={`${slot.venue}-${slot.start}-${slot.name}`}
                          slot={slot}
                          showVenue
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {VENUE_ORDER.map((vk) => {
                  const rows = day.slots.filter((s) => s.venue === vk);
                  if (!rows.length) return null;
                  const v = VENUES[vk];
                  return (
                    <div key={vk} className="relative z-10 pt-6 first:pt-0 pb-2">
                      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 mb-2.5">
                        <h2
                          className="font-['Anton'] uppercase tracking-wide text-2xl md:text-4xl leading-none"
                          style={{ color: NAVY }}
                        >
                          {v.name}
                        </h2>
                        <span className="text-[11px] md:text-sm font-semibold" style={{ color: `${NAVY}8C` }}>
                          {v.sub}
                        </span>
                      </div>
                      <div className="space-y-2 md:space-y-2.5">
                        {rows.map((slot) => (
                          <ActBlock
                            key={`${slot.start}-${slot.name}`}
                            slot={slot}
                            showVenue={false}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PosterPanel>

          {/* Footnotes */}
          <p className="text-yacht-charcoal/45 text-xs md:text-sm mt-8 leading-relaxed">
            "To Be Announced" slots are still being booked — more acts coming.
            Admirals Lounge is open to VIP ticket holders. Schedule subject to
            change.
          </p>

          <div className="mt-8 md:mt-12 flex flex-col sm:flex-row gap-4">
            <a
              href="/lineup"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-yacht-charcoal text-yacht-charcoal font-bold text-base hover:bg-yacht-charcoal hover:text-yacht-cream transition-colors"
            >
              Meet the Artists
            </a>
            <a
              href="/tickets"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base hover:brightness-110 transition-all"
              style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
            >
              Get Tickets →
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Schedule;
